import Debug from 'debug';
import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { EMPTY_ARRAY, EMPTY_STRING, extractTextFromScripts } from '../utils';
import { notificationService } from '../services/notificationService';
import { blobRegistry } from '../services/BlobRegistry';
import { Refreshable, RefreshSignalMixin } from '../flow/_refreshSignalMixin';
import { dataBroker } from '../services/DataBroker';

const log = Debug('pdq:component');

const defaultRender = (module: any) => html`<div>No render method registered...</div>`;



@customElement('pdq-component')
export class PdqComponent extends RefreshSignalMixin(LitElement) implements Refreshable {
  #unsubscribe = () => {}
  #lastImportedScriptHash: symbol | string = Symbol('NO_SCRIPT');

  static nextId = 0;
  id = `pdq-component-${++PdqComponent.nextId}`;

  @state()
  data = {}

  @state()
  _render = defaultRender

  refresh() {
    this.requestUpdate()
  }

  render() {
    const variablesSlot = html`<slot style="display: none;" name="variables" @slotchange="${this._onVariablesSlotChange}"></slot>`;
    const scriptSlot = html`<slot style="display: none;" name="script" @slotchange="${this._onScriptsSlotChange}"></slot>`;

    return html`
      ${variablesSlot}
      ${scriptSlot}
      ${this._render.call(this, html)}
    `;
  }

  private async _onScriptsSlotChange(event: Event) {
    log(`${this.id} - slot change`);
    const slot = event.target as HTMLSlotElement;
    const nodes = slot.assignedNodes({ flatten: true });
    const script = extractTextFromScripts(nodes);


    if (!script) {
      this._render = defaultRender
      return
    }

    const meta = await blobRegistry.registerStringAsBlob(script, "application/javascript")
    let module;
    try {
      module = await import(meta.url);
    } catch (e) {
      log(`WARNING: failed to import script: ${e}`)
      console.log(`WARNING: failed to import script: ${e}`, this._script, e)
      return
    }

    if (meta.hash !== this.#lastImportedScriptHash && module.init) {
      log('script found for the first time... calling init...')
      try {
        await module.init.call(this, import.meta.url)
      } catch (e) {
        log(`WARNING: init script failed: ${e}`)
        console.log(`WARNING: init script failed: ${e}`)
      }
    } else if (!module.init) {
      log(`INFO: no init function exported from script`)
    }

    this.#lastImportedScriptHash = meta.hash
    if (!module.render) {
      log(`WARNING: no render function exported from script`)
      this._render = defaultRender
      return
    }

    this._render = module.render
    this.requestUpdate('_render');
  }

  private async _onVariablesSlotChange(event: Event) {
    log(`${this.id} - variables slot change`);
    const slot = event.target as HTMLSlotElement;
    const nodes = slot.assignedNodes({ flatten: true });
    const template = extractTextFromScripts(nodes);
    try {
      const vars = JSON.parse(template);
      if (Array.isArray(vars)) {
        log('variables switching to', vars);
        this.#unsubscribe()


        const observables = await Promise.all(vars.map(async i => ({name: i, data: await dataBroker.getObservableDataSource(i)})));
        const subscriptions = observables.map(i => i.data.subscribe((data) => {
          const ndata = {} as any
          ndata[i.name] = data
          const oldData = this.data
          this.data = {...this.data, ...ndata }
          this.requestUpdate('data', oldData)
        }))
        this.#unsubscribe = () => {
          log('unsubscribing from observables')
          debugger
          subscriptions.forEach(i => i.unsubscribe())
        }
      } else {
        log('variables not an array... ignoring...', vars);
      }
    } catch (e: any) {
      log(`Error parsing JSON: \n${e?.message || e}`);
      notificationService.error(`pdq-component#${this.id}|Error parsing JSON: \n${e?.message || e}`);
    }
  }


  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unsubscribe()
  }

}


@customElement('pdq-component-factory')
export class PdqComponentFactory extends LitElement {
  #lastImportedScriptHash: string = EMPTY_STRING;

  @property({ type: String }) name = EMPTY_STRING
  @property({ type: Array }) properties = EMPTY_ARRAY


  render() {
    const scriptSlot = html`<slot style="display: none;" name="script" @slotchange="${this._onScriptsSlotChange}"></slot>`;
    return html`${scriptSlot}`;
  }


  private registerComponent(
    name: string,
    render: (html: (strings: TemplateStringsArray, ...values: unknown[]) => TemplateResult<1>) => TemplateResult,
    ctor: () => void,
    styles: any
    ) {
      if (customElements.get(name)) {
        return;
      }

      const className = `Dynamic${name.split('-').map(part =>
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      ).join('')}Component`;

      const factory = this;
      const props = [...this.properties]
      const DynamicComponentClass = {
        [className]: class extends LitElement {
          constructor() {
            super();
            props.forEach(prop => {
              Object.defineProperty(this, `_${prop}`, {
                value: this.getAttribute(prop) || factory.getAttribute(prop) || '',
                writable: true
              });
            });
            ctor.call(this)
          }
        }
      }[className];

      // Add getters and setters for each property
      this.properties.forEach(prop => {
        Object.defineProperty(DynamicComponentClass.prototype, prop, {
          get() {
            return this[`_${prop}`];
          },
          set(value: any) {
            const oldValue = this[`_${prop}`];
            this[`_${prop}`] = value;
            this.requestUpdate(prop, oldValue);
          }
        });
      });

      // Add the render method
      DynamicComponentClass.prototype.render = function() {
        return render.call(this, html);
      };

      debugger
      if (styles)
        DynamicComponentClass.styles = styles

      // Register the component
      customElements.define(name, DynamicComponentClass);
  }


  private async _onScriptsSlotChange(event: Event) {
    log(`${this.id} - slot change`);
    const slot = event.target as HTMLSlotElement;
    const nodes = slot.assignedNodes({ flatten: true });
    const script = extractTextFromScripts(nodes);


    if (!script) {
      log(`WARNING: no script in slot`)
      console.log(`WARNING: no script in slot`)
      return
    }

    const meta = await blobRegistry.registerStringAsBlob(script, "application/javascript")
    let module;
    try {
      module = await import(meta.url);
    } catch (e) {
      log(`WARNING: failed to import script: ${e}`)
      console.log(`WARNING: failed to import script: ${e}`)
      return
    }

    if (meta.hash !== this.#lastImportedScriptHash && module.init) {
      log('script found for the first time... calling init...')
      try {
        await module.init.call(this, import.meta.url)
      } catch (e) {
        log(`WARNING: init script failed: ${e}`)
        console.log(`WARNING: init script failed: ${e}`)
      }
    } else if (!module.init) {
      log(`INFO: no init function exported from script`)
    }

    this.#lastImportedScriptHash = meta.hash
    if (!module.render) {
      log(`WARNING: no render function exported from script`)
      return
    }

    if (!this.name) {
      log(`WARNING: no name property set`)
      return
    }
    const nameParts = this.name.split('-')
    if (nameParts.length < 2 || nameParts[0].length < 3) {
      log(`WARNING: name property must be in the format <namespace>-<component> and namespace must be at least 3 characters long`)
      return
    }

    let ctor = function () {}
    if (module.constructor)
      ctor = module.constructor

    this.registerComponent(this.name, module.render, ctor, module.styles)
  }

}
